/*
  ITGC Approve modal — whole-request decision. The lines are READ-ONLY; the
  approver reviews them and commits ONE decision for the entire request:
    • Approve        -> every line is granted (POST /approve, decision=APPROVED)
    • Reject request -> the whole request is rejected (decision=REJECTED)
  An optional approver note is attached to either decision.
*/
import React, { useEffect, useMemo, useState } from 'react';
import { Ban, Check } from 'lucide-react';
import { itgcService, AccessRequest } from '../../services/itgcService';
import Modal from '../common/Modal';
import Button from '../common/Button';
import Badge from '../common/Badge';
import Toast, { ToastMessage } from '../common/Toast';
import NoDataFound from '../common/NoDataFound';
import { statusVariant } from '../../utils/statusVariant';
import { usePermission } from '../../contexts/PermissionContext';
import { useFieldValidation } from '../../validation/useFieldValidation';
import { ValidatedTextarea } from '../../validation/ValidatedFields';
import { confirmWarningWithHtml } from '../common/confirm';
import extractApiError from '../../utils/apiError';

// Coerce any error to a string — the shared validation engine returns detail as
// an array/object on 422, which crashes <Toast> if rendered raw.
const extractErr = (e: any, fallback: string): string => {
  const s = extractApiError(e, '');
  if (s) return s;
  const errStr = e?.response?.data?.error;
  if (typeof errStr === 'string' && errStr) return errStr;
  return fallback;
};

interface Props {
  requestId: number;
  onClose: () => void;
  onSaved: () => void;
}

const ITGCApproveModal: React.FC<Props> = ({ requestId, onClose, onSaved }) => {
  const canApprove = usePermission('ORG_ITGC_ACCESS_APPROVE', 'view+modify').allowed;
  const vh = useFieldValidation();

  const [req, setReq] = useState<AccessRequest | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<ToastMessage | null>(null);

  useEffect(() => { vh.reset(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    (async () => {
      try {
        const r = await itgcService.getRequest(requestId);
        setReq(r);
      } catch (e: any) {
        setToast({ type: 'error', message: extractErr(e, 'Failed to load request') });
      } finally { setLoaded(true); }
    })();
  }, [requestId]);

  const lines = useMemo(() => (req?.lines || []).filter(l => l.is_active === 1), [req]);
  const total = lines.length;

  // Commit one decision for the whole request.
  const decide = async (decision: 'APPROVED' | 'REJECTED') => {
    if (!canApprove) { setToast({ type: 'error', message: 'Not authorised to approve' }); return; }
    if (!vh.validateAll([{ name: 'approver_note', code: 110, value: note }])) {
      setToast({ type: 'error', message: 'Please fix the highlighted fields' }); return;
    }
    setBusy(true);
    try {
      await itgcService.approveRequest(requestId, decision, note || undefined);
      onSaved();
    } catch (e: any) {
      setToast({ type: 'error', message: extractErr(e, decision === 'APPROVED' ? 'Approve failed' : 'Reject failed') });
    } finally { setBusy(false); }
  };

  const approve = () => {
    if (total === 0) { setToast({ type: 'error', message: 'This request has no lines to approve.' }); return; }
    decide('APPROVED');
  };

  const reject = async () => {
    if (!canApprove) { setToast({ type: 'error', message: 'Not authorised to approve' }); return; }
    const ok = await confirmWarningWithHtml({
      title: 'Reject the whole request?',
      html: 'This <strong>rejects the request</strong> — nothing will be granted.',
    });
    if (!ok) return;
    decide('REJECTED');
  };

  if (!canApprove) {
    return (
      <Modal isOpen onClose={onClose} size="md">
        <Modal.Header>Approve access request</Modal.Header>
        <Modal.Body><NoDataFound title="Not authorised" message="You are not authorised to approve ITGC access requests." /></Modal.Body>
      </Modal>
    );
  }

  return (
    <>
      <Modal isOpen onClose={onClose} size="lg">
        <Modal.Header>
          <span className="flex items-center gap-2">
            Approve {req?.request_code || '…'}
            {req && <Badge variant={statusVariant(req.status)} size="sm">{req.status}</Badge>}
          </span>
        </Modal.Header>
        <Modal.Body>
          {!loaded ? <p className="text-sm text-gray-500">Loading…</p> : (
            <>
              {/* read-only header: request meta stacked on the LEFT, approver note on the RIGHT */}
              <div className="grid grid-cols-2 gap-x-6 mb-3 items-start">
                <div className="flex flex-col gap-2">
                  <div><span className="text-xs text-gray-500">Title</span><div className="text-sm">{req?.title || '—'}</div></div>
                  <div><span className="text-xs text-gray-500">Initiator</span><div className="text-sm">{req?.initiator_name || `#${req?.initiator_user_id}`}</div></div>
                  <div><span className="text-xs text-gray-500">Justification</span><div className="text-sm whitespace-pre-wrap">{req?.justification || '—'}</div></div>
                </div>
                <div>
                  <ValidatedTextarea
                    vh={vh}
                    name="approver_note"
                    code={110}
                    label="Approver note"
                    value={note}
                    onChange={e => setNote(e.target.value)}
                    rows={5}
                    placeholder="Optional — context for the decision"
                    data-testid="itgc-approve-note"
                  />
                </div>
              </div>

              <div className="table-list">
                <div className="table-list-scroll">
                  <table className="table-list-table">
                    <thead>
                      <tr>
                        <th className="table-list-th">Type</th>
                        <th className="table-list-th">Target</th>
                        <th className="table-list-th">Action</th>
                        <th className="table-list-th">Remarks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lines.length === 0 && (
                        <tr><td colSpan={4} className="table-list-cell"><NoDataFound title="No Lines" message="This request has no lines." /></td></tr>
                      )}
                      {lines.map(l => (
                        <tr key={l.id} className="table-list-row" data-testid={`itgc-approve-line-${l.id}`}>
                          <td className="table-list-cell">{l.target_type}</td>
                          <td className="table-list-cell"><span className="table-list-cell-title">{l.target_label || `#${l.target_id}`}</span></td>
                          <td className="table-list-cell">
                            <span className={`status-badge xs ${l.action === 'GRANT' ? 'success' : l.action === 'REVOKE' ? 'danger' : 'neutral'}`}>{l.action}</span>
                          </td>
                          <td className="table-list-cell table-list-cell--truncate" title={l.line_remarks}>{l.line_remarks || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="app-modal-footer--split">
          <span className="text-sm text-gray-500" data-testid="itgc-approve-summary">
            {total === 0
              ? 'No lines to decide'
              : `${total} line${total === 1 ? '' : 's'} in this request`}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="danger"
              icon={Ban}
              onClick={reject}
              disabled={busy || !loaded}
              data-testid="itgc-reject-all"
            >
              Reject request
            </Button>
            <Button
              variant="success"
              icon={Check}
              onClick={approve}
              disabled={busy || !loaded || total === 0}
              data-testid="itgc-approve-submit"
            >
              Approve
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
      <Toast toast={toast} onClose={() => setToast(null)} />
    </>
  );
};

export default ITGCApproveModal;
